// Pagina import massivo aziende da Excel con drag-and-drop
import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDropzone } from 'react-dropzone'
import { useMutation } from '@tanstack/react-query'
import { Upload, FileSpreadsheet, Download, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import api from '../../lib/api'

interface ImportRowError {
  row: number
  vat: string
  message: string
}

interface ImportResult {
  companiesCreated: number
  companiesUpdated: number
  sitesCreated: number
  sitesUpdated: number
  contactsCreated: number
  errors: ImportRowError[]
}

/**
 * Pagina per l'import massivo di aziende e sedi da file Excel.
 * Supporta drag-and-drop e mostra i risultati dell'operazione.
 */
export function CompanyImportPage() {
  const navigate = useNavigate()
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [result, setResult] = useState<ImportResult | null>(null)

  // Mutation per upload file
  const importMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file)
      const res = await api.post<ImportResult>('/companies/import/excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return res.data
    },
    onSuccess: (data) => {
      setResult(data)
    },
  })

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0] ?? null)
      setResult(null)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  })

  const handleDownloadTemplate = async () => {
    try {
      const res = await api.get('/companies/import/template', { responseType: 'blob' })
      const url = window.URL.createObjectURL(new Blob([res.data as BlobPart]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'kleisnote-import-aziende.xlsx')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      // Errore download template — silenzioso
    }
  }

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile)
    }
  }

  const hasSuccess = result && (
    result.companiesCreated > 0 ||
    result.companiesUpdated > 0 ||
    result.sitesCreated > 0 ||
    result.sitesUpdated > 0
  )

  return (
    <div className="space-y-4 max-w-3xl">
      <PageHeader
        title="Importa clienti da Excel"
        breadcrumb={[
          { label: 'Clienti', href: '/companies' },
          { label: 'Importa da Excel' },
        ]}
      />

      {/* Istruzioni + download template */}
      <Card>
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1">
            <h3 className="text-sm font-semibold text-[var(--fc-foreground)]">Come funziona</h3>
            <ul className="text-sm text-[var(--fc-foreground-muted)] space-y-0.5 list-disc list-inside">
              <li>Scarica il template Excel e compila i dati</li>
              <li>La colonna <strong>partita_iva</strong> è obbligatoria (discriminante upsert)</li>
              <li>Puoi avere pi&ugrave; righe con la stessa P.IVA per sedi diverse</li>
              <li>I record esistenti vengono aggiornati, quelli nuovi creati</li>
            </ul>
          </div>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Download className="h-4 w-4" />}
            onClick={() => void handleDownloadTemplate()}
            className="shrink-0"
          >
            Scarica template
          </Button>
        </div>
      </Card>

      {/* Area drag-and-drop */}
      <Card noPadding>
        <div
          {...getRootProps()}
          className={`
            flex flex-col items-center justify-center gap-3 p-10 rounded-lg border-2 border-dashed cursor-pointer transition-colors
            ${isDragActive
              ? 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_5%,transparent)]'
              : 'border-[var(--fc-border)] hover:border-[var(--fc-primary)] hover:bg-[var(--fc-surface-overlay)]'
            }
          `}
        >
          <input {...getInputProps()} aria-label="Carica file Excel" />
          {selectedFile ? (
            <>
              <FileSpreadsheet className="h-12 w-12 text-[var(--fc-primary)]" aria-hidden="true" />
              <div className="text-center">
                <p className="font-medium text-[var(--fc-foreground)]">{selectedFile.name}</p>
                <p className="text-sm text-[var(--fc-foreground-muted)]">
                  {(selectedFile.size / 1024).toFixed(1)} KB — Clicca per cambiare file
                </p>
              </div>
            </>
          ) : (
            <>
              <Upload
                className={`h-12 w-12 ${isDragActive ? 'text-[var(--fc-primary)]' : 'text-[var(--fc-foreground-muted)]'}`}
                aria-hidden="true"
              />
              <div className="text-center">
                <p className="font-medium text-[var(--fc-foreground)]">
                  {isDragActive ? 'Rilascia il file qui' : 'Trascina il file Excel oppure clicca'}
                </p>
                <p className="text-sm text-[var(--fc-foreground-muted)]">
                  Supporta .xlsx e .xls fino a 10 MB
                </p>
              </div>
            </>
          )}
        </div>
      </Card>

      {/* Azioni */}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => navigate('/companies')}>
          Annulla
        </Button>
        <Button
          onClick={handleImport}
          disabled={!selectedFile || importMutation.isPending}
          leftIcon={<Upload className="h-4 w-4" />}
        >
          {importMutation.isPending ? 'Importazione in corso...' : 'Avvia importazione'}
        </Button>
      </div>

      {/* Errore upload */}
      {importMutation.isError && (
        <Alert variant="error">
          Errore durante l&apos;importazione. Verificare il file e riprovare.
        </Alert>
      )}

      {/* Risultati */}
      {result && (
        <Card>
          <h3 className="text-sm font-semibold text-[var(--fc-foreground)] mb-3">Risultati importazione</h3>

          {/* Contatori */}
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 mb-4">
            {[
              { label: 'Aziende create', value: result.companiesCreated, color: 'text-[var(--fc-success)]' },
              { label: 'Aziende aggiornate', value: result.companiesUpdated, color: 'text-[var(--fc-primary)]' },
              { label: 'Sedi create', value: result.sitesCreated, color: 'text-[var(--fc-success)]' },
              { label: 'Sedi aggiornate', value: result.sitesUpdated, color: 'text-[var(--fc-primary)]' },
              { label: 'Referenti creati', value: result.contactsCreated, color: 'text-[var(--fc-success)]' },
              { label: 'Errori riga', value: result.errors.length, color: result.errors.length > 0 ? 'text-[var(--fc-danger)]' : 'text-[var(--fc-foreground-muted)]' },
            ].map(({ label, value, color }) => (
              <div key={label} className="rounded border border-[var(--fc-border)] p-2 text-center">
                <p className={`text-xl font-bold ${color}`}>{value}</p>
                <p className="text-xs text-[var(--fc-foreground-muted)]">{label}</p>
              </div>
            ))}
          </div>

          {/* Errori per riga */}
          {result.errors.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1.5 text-sm font-medium text-[var(--fc-danger)]">
                <AlertTriangle className="h-4 w-4" />
                Errori per riga ({result.errors.length})
              </div>
              <div className="max-h-60 overflow-y-auto rounded border border-[var(--fc-border)]">
                <table className="w-full text-xs">
                  <thead className="bg-[var(--fc-surface-overlay)]">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Riga</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">P.IVA</th>
                      <th className="px-3 py-2 text-left font-medium text-[var(--fc-foreground-muted)]">Errore</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.errors.map((err, i) => (
                      <tr key={i} className="border-t border-[var(--fc-border)]">
                        <td className="px-3 py-1.5 text-[var(--fc-foreground-muted)]">{err.row}</td>
                        <td className="px-3 py-1.5 font-mono text-[var(--fc-foreground)]">{err.vat || '—'}</td>
                        <td className="px-3 py-1.5 text-[var(--fc-danger)]">{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* CTA dopo successo */}
          {hasSuccess && (
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--fc-success)]">
              <CheckCircle className="h-4 w-4" />
              Importazione completata con successo.
            </div>
          )}

          {!hasSuccess && result.errors.length > 0 && (
            <div className="mt-4 flex items-center gap-2 text-sm text-[var(--fc-danger)]">
              <XCircle className="h-4 w-4" />
              Nessun record importato. Correggere gli errori e riprovare.
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <Button variant="secondary" onClick={() => { setResult(null); setSelectedFile(null) }}>
              Importa altro file
            </Button>
            {hasSuccess && (
              <Button onClick={() => navigate('/companies')}>
                Vai ai clienti
              </Button>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
